import { Component, OnInit, Inject, Input, EventEmitter, Output, OnDestroy } from '@angular/core';
import { QuoteService } from '../../../../quote/services/quote.service';
import { Router, ActivatedRoute } from '@angular/router';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { QuoteArea } from '../../../models/quote.area.interface';
import { Quote } from '../../../models/quote.interface';
import { AreaSummary } from '../../../../quote/models/area.summary.interface';
import { LostReasonType } from '../../../models/lost.reason.type.interface';
import { Competitor } from '../../../models/competitor.interface';
import { QuoteAreaDialog } from '../quote-area/quote-area.dialog';
import { QuoteBase } from '../../../models/quote.base.interface';
import { NgForm } from '@angular/forms';
import { ConvertDialog } from '../../../../quote/convert-dialog/convert.dialog';
import { Area } from '../../../models/area.interface';
import { ContactSelect } from '../../../../quote/models/contact.select.interface';
import { FileService } from '../../../services/file.service';
import { QuoteSummary } from '../../../../quote/models/quote.summary.interface';
import { DeviceDetectorService } from 'ngx-device-detector';
import { AbstractBiditComponent } from '../../abstract.bidit.component';
import { QuoteTemplateDialog } from '../create-quote-template/create-quote-template.dialog';
import { PrintOptionsDialog } from '../../print-options-dialog/print.options.dialog';
import { PrintLanguageDialog } from '../../../../shared/controls/print-language-dialog/print.language.dialog';
import { finalize, Subscription } from 'rxjs';
import { AuthService } from '../../../services/auth.service';
import { RolePermission } from '../../../models/role.permission.enum';
import { DealerDocument } from '../../../models/dealer.document.interface';
import { QuoteTaxOptionsDialog } from '../quote-tax-options/quote-tax-options.dialog';
import { QuoteAreaItem } from '../../../models/quote.area.item.interface';
import { CdkDragDrop, CdkDropList, CdkDrag, moveItemInArray } from '@angular/cdk/drag-drop';
import { DealerService } from '../../../../dealer/services/dealer.service';
import { CalculateService } from '../../../services/calculate.service';
import { QuoteItemNoteTemplate } from '../../../models/quote.item.note.template.interface';
import { QuoteAssignment } from '../../../models/quote.assignment';
import { QuoteAssignmentSelect } from '../../../../quote/models/quote.assignment.select.interface';

export interface StatusDialogData {
  quote_ID: number;
  quote_IsLost: boolean;
  lostReasonType_ID: number;
  competitor_ID: number;
  quote_LostNote: string;

  lostReasonTypes: LostReasonType[];
  competitors: Competitor[];
}
export interface AreaDialogData {
  quoteId: number;
  quoteAreaItemId: number;
  quoteArea: QuoteArea;
  areas: Area[];
  showLinePricing: boolean;
  quoteItemNoteTemplates: QuoteItemNoteTemplate[];
}
export interface CopyDialogData {
  quote_ID: number;
  quoteCopy_Name: string;
}
export interface EmailDialogData {
  quote_ID: number;
  changeOrder_ID?: number;
  quote_ContractNotes: string;
  contacts: ContactSelect[];
  quoteEmail_Message: string;
  docuSignEnabled: boolean;
  sendWithSignatureBlock: boolean;
  showPricing: boolean;
  showSquareFootage: boolean;
  showTaxes: boolean;
  groupAreas: boolean;
  hideLineItemNotes: boolean;
  quoteAssignments: QuoteAssignmentSelect[];
}

@Component({
  selector: 'app-quote-details',
  templateUrl: './quote-details.component.html',
  styleUrls: ['./quote-details.component.scss']
})
export class QuoteDetailsComponent extends AbstractBiditComponent implements OnInit {
  quote: Quote = {} as Quote;
  areaSummaries: AreaSummary[];
  areas: Area[];
  lostReasonTypes: LostReasonType[];
  competitors: Competitor[];
  quoteItemNoteTemplates: QuoteItemNoteTemplate[];

  quote_status: any = {};

  quoteMarginAmount: number;
  quoteMargin: number;
  quoteBidAmount: number;
  quoteBidTotalAmount: number

  lostStatus: string;
  acknowledgementStatus: string = "";
  acknowledgementDate: string = "";

  isQuoteAcknowledged: boolean = false;
  readOnly: boolean = true;
  canAddJob: boolean = false;
  canAddQuote: boolean = false;

  quoteOnly: boolean = false;
  showMarginEdit: boolean = false;
  showBidAmountEdit: boolean = false;
  dealerRoundingType: number;
  showQuoteAnalysis: boolean = false;
  showLinePricing: boolean = false;
  showCostBreakdown: boolean = false;
  showMargins: boolean = false;
  allowPrint: boolean = false;

  constructor(private dialog: MatDialog, private quoteService: QuoteService, private fileService: FileService,
    private dealerService: DealerService,
    private calculateService: CalculateService,
    deviceDetectorService: DeviceDetectorService,
    private router: Router, private route: ActivatedRoute) {

    super(deviceDetectorService);
  }

  @Input()
  public set id(value: number) {
    if (value) {
      this.refreshQuote(value);
    }
  }

  @Input()
  public set quoteSummary(value: QuoteSummary) {
    if (value && value.quote.quote_ID) {
      // clone object to keep changes local
      let quoteSummary = (<any>Object).assign({}, value) as QuoteSummary;

      this.loadQuote(quoteSummary);
    }
  }

  @Output() updated = new EventEmitter<QuoteBase>();
  @Output() toggleTemplate = new EventEmitter<number>();

  ngOnInit(): void {

    this.showQuoteAnalysis = QuoteService.hasPermission(RolePermission.QuoteAnalysis);
    this.showLinePricing = QuoteService.hasPermission(RolePermission.ShowLineItemPricing);
    this.showMargins = QuoteService.hasPermission(RolePermission.ViewEditMargins);
    this.showCostBreakdown = QuoteService.hasPermission(RolePermission.ShowCostBreakdown);
    this.allowPrint = QuoteService.hasPermission(RolePermission.ShowLineItemPricing) && QuoteService.hasPermission(RolePermission.ShowCostBreakdown) &&
      QuoteService.hasPermission(RolePermission.ViewEditMargins);

    this.quoteService.getLostReasonTypes()
      .subscribe({
        next: (data) => {
          this.lostReasonTypes = data;
        },
        error: (err) => this.onError(err)
      });

    this.quoteService.getQuoteItemNoteTemplates()
      .subscribe({
        next: (data) => {
          this.quoteItemNoteTemplates = data;
        },
        error: (err) => this.onError(err)
      });

    this.quoteService.getCompetitors()
      .subscribe({
        next: (data) => {
          this.competitors = data;
        }
      });

    this.quoteService.getAreas()
      .subscribe({
        next: (data) => {
          this.areas = data;
        },
        error: (err) => this.onError(err)
      });

    this.readOnly = !QuoteService.hasPermission(RolePermission.EditQuote);
    this.canAddJob = QuoteService.hasPermission(RolePermission.AddJob);
    this.canAddQuote = QuoteService.hasPermission(RolePermission.AddQuote);
    this.quoteOnly = QuoteService._authUserSource.value.isQuoteOnly;
  }

  dropArea(event: CdkDragDrop<QuoteArea[]>) {
    if (event.previousIndex !== event.currentIndex) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      this.quoteService.resequenceQuoteAreas(this.quote.quote_ID, event.container.data).subscribe({
        next: (data) => {
          this.refreshQuote(this.quote.quote_ID);
        },
        error: (err) => this.onError(err)
      });
    }
  }

  dropItem(event: CdkDragDrop<QuoteAreaItem[]>) {
    if (event.previousIndex !== event.currentIndex) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      this.quoteService.resequenceQuoteQuoteAreaItems(this.quote.quote_ID, event.container.data).subscribe({
        next: (data) => {
          this.refreshQuote(this.quote.quote_ID);
        },
        error: (err) => this.onError(err)
      });
    }
  }

  refreshQuote(quoteId: number) {
    this.quoteService.getQuote(quoteId)
      .subscribe({
        next: (data) => {
          this.quote = data.quote;
          this.areaSummaries = data.areaSummaries;
          this.calculateQuote();
          this.isQuoteAcknowledged = (this.quote.acknowledgementStatus != 'Pending' || this.quote.acknowledgementIsExpired);
          this.readOnly = ((this.quote.acknowledgementStatus == 'Accepted' || this.quote.acknowledgementStatus == 'Pending' && !this.quote.acknowledgementIsExpired));
          
          this.quoteBidTotalAmount = this.quote.quote_BidAmount + this.quote.netTax_Total;
        },
        error: (err) => this.onError(err)
      });
  }

  loadQuote(quoteSummary: QuoteSummary) {
    this.quote = quoteSummary.quote;
    this.areaSummaries = quoteSummary.areaSummaries;
    
    if (this.canAddQuote && this.areaSummaries.length === 0) {
      this.readOnly = false;
    }

    if (this.quote.quote_Converted != null || ((this.quote.acknowledgementStatus == 'Accepted' || this.quote.acknowledgementStatus == 'Pending' && !this.quote.acknowledgementIsExpired)))
      this.readOnly = true;

    this.isQuoteAcknowledged = (this.quote.acknowledgementStatus != 'Pending' || this.quote.acknowledgementIsExpired);
    this.getDealerRoundingType();

    this.calculateQuote();
  }

  onAreaUpdated(quoteArea: QuoteArea) {
    this.refreshQuote(this.quote.quote_ID);
  }

  onAreaCanceled(quoteAreaParam: QuoteArea) {
    let area = this.areaSummaries.find(a => a.area_ID == quoteAreaParam.area_ID);
    let quoteArea = area.quoteAreas.find(qa => qa.quoteArea_ID == quoteAreaParam.quoteArea_ID);

    if (quoteArea) {
      quoteArea.showEdit = false;
    }
  }

  recalcQuote() {    
      this.quoteService.recalcQuote(this.quote.quote_ID)
        .subscribe({
          next: (data) => {
            this.refreshQuote(data);
          },
          error: (err) => this.onError(err)
        });
}


  getDealerRoundingType(): void {
    this.dealerService.getDealerDetails()
      .subscribe(result => {
        this.dealerRoundingType = result.dealer_Rounding;
      });
  }

  calculateQuote() {
    this.lostStatus = (this.quote.quote_IsLost ? 'L' : '');

    this.quoteMarginAmount = this.quote.quote_BidAmount - this.quote.totalCost;
    this.quoteMargin = (100 - this.quote.quote_GrossMargin) / 100;
    this.quoteBidTotalAmount = this.quote.quote_BidAmount + this.quote.netTax_Total;

    this.areaSummaries.forEach(a => {
      if (a.area_IsOption) {
        a.totalCost = 0;

        a.quoteAreas.forEach(qa => {
          a.totalCost += qa.quoteItem.optionBid;
        });

      } else {
        a.quoteAreas.forEach(qa => {
          qa.quoteUpgrades.forEach(u => {
            u.upgradeBid = (u.area_TotalCost / this.quoteMargin) - (qa.quoteItem.area_TotalCost / this.quoteMargin);
            u.upgradeDelta = (u.area_TotalCost - qa.quoteItem.area_TotalCost);
          });
        });
      }
    });
  }

  toggleArea(quoteArea: QuoteArea) {
    quoteArea.showEdit = true;
  }

  editMargin() {
    this.showMarginEdit = true;
  }

  editBid() {
    this.showBidAmountEdit = true;
  }

  updateQuoteMargin() {
    this.quoteService.updateQuoteMargin(this.quote.quote_ID, this.quote.quote_GrossMargin)
      .subscribe({
        next: (data) => {
          this.quote.quote_BidAmount = data.quote_BidAmount;

          this.calculateQuote();
          this.showMarginEdit = false;
        },
        error: (err) => this.onError(err)
      });
  }

  updateBidAmount() {
    this.quote.quote_BidAmount = this.calculateService.GetRoundedAmount(this.dealerRoundingType, this.quote.quote_BidAmount);

    this.quoteService.updateQuoteBidAmount(this.quote.quote_ID, this.quote.quote_BidAmount)
      .subscribe({
        next: (data) => {
          this.quote.quote_GrossMargin = data.quote_GrossMargin;

          this.calculateQuote();
          this.showBidAmountEdit = false;
        },
        error: (err) => this.onError(err)
      });
  }

  materialsRoundingChanged() {
    this.quoteService.updateQuoteMaterialRounding(this.quote.quote_ID, this.quote.roundMaterials)
      .subscribe({
        next: (data) => {
          this.refreshQuote(this.quote.quote_ID);
        },
        error: (err) => this.onError(err)
      });
  }

  deleteQuote(): void {
    var proceed = confirm($localize`Are you sure?`);
    if (!proceed) return;

    this.quoteService.deleteQuote(this.quote.quote_ID)
      .subscribe({
        next: (data) => this.router.navigate(['/quote/']),
        error: (err) => this.onError(err)
      });
  }

  openQuoteTaxDialog(): void {
    const dialogRef = this.dialog.open(QuoteTaxOptionsDialog, {
      width: '900px',
      autoFocus: false,
      data: {
        quote_ID: this.quote.quote_ID,
        materialTax_Rate: this.quote.materialTax_Rate ?? null,
        materialTax_Total: this.quote.materialTax_Total ?? null,
        laborTax_Rate: this.quote.laborTax_Rate ?? null,
        laborTax_Total: this.quote.laborTax_Total ?? null,
        otherTax_Rate: this.quote.otherTax_Rate ?? null,
        otherTax_Total: this.quote.otherTax_Total ?? null,
        netTax_Total: this.quote.netTax_Total ?? null,

        material_Cost: this.quote.materialCost,
        labor_Cost: this.quote.laborCost,
        other_Cost: this.quote.otherCost
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The quote tax dialog was closed');
      if (result) {
        this.refreshQuote(this.quote.quote_ID);
      };
    });
  }

  quoteAnalysis(): void {
    const dialogRef = this.dialog.open(PrintLanguageDialog);
    dialogRef.afterClosed().subscribe(result => {
      if (!result) return;
      this.isRequesting = true;

      this.quoteService.quoteAnalysis(this.quote.quote_ID, null, result)
        .subscribe({
          next: (data) => {
            this.isRequesting = false;
            this.fileService.downloadFile(data, 'QuoteAnalysis.pdf')
          },
          error: (err) => this.onError(err)
        });
    });
  }

  acknowledgeQuote(status: number): void {
    var proceed = confirm($localize`Are you sure?`);
    if (!proceed) return;

    this.quoteService.acknowledgeQuote(this.quote.quote_ID, status)
      .subscribe({
        next: (result) => {
          this.refreshQuote(this.quote.quote_ID);
          this.updated.emit(this.quote);
        },
        error: (err) => this.onError(err)
      });
  }

  printQuote(): void {
    const dialogRef = this.dialog.open(PrintLanguageDialog);
    dialogRef.afterClosed().subscribe(result => {
      if (!result) return;
      this.isRequesting = true;

      this.quoteService.printQuote(this.quote.quote_ID, null, result)
        .subscribe({
          next: (data) => {
            this.isRequesting = false;
            this.fileService.downloadFile(data, 'QuoteContract.pdf');
          },
          error: (err) => this.onError(err)
        });
    });
  }

  openPrintOptionsDialog(): void {
    const dialogRef = this.dialog.open(PrintOptionsDialog, {
      width: '500px',
      data: {
        quote_ID: this.quote.quote_ID,
        quote_ContractNotes: this.quote.quote_ContractNotes,
        showPrintAttachments: true,
        showTaxes: false,
        hideLineItemNotes: false,
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
      if (result) {
        this.quote.quote_ContractNotes = result.quote_ContractNotes;

        window.alert($localize`Quote Printed`);
        this.isRequesting = false;
      }
    });
  }

  quoteStatusChanged(): void {
    if (this.lostStatus == 'L') {
      this.openStatusDialog();
      return;
    }

    if (this.lostStatus == 'W') {
      this.openConvertDialog();
      return;
    }

    this.quote.lostReasonType_ID = null;
    this.quote.competitor_ID = null;
    this.quote.quote_IsLost = false;

    this.quoteService.updateQuoteLostStatus(this.quote.quote_ID, this.quote)
      .subscribe({
        next: (data) => {
          this.updated.emit(this.quote);
        },
        error: (error: string) => this.onError(error)
      });
  }

  openConvertDialog(): void {
    this.showDialog('Mark quote as won?', 'Complete Quote', 'OK', 'Cancel')
      .subscribe((data) => {
        if (!data) {
          this.lostStatus = '';
          return;
        }

        this.isRequesting = true;
        this.quoteService.convertQuote(this.quote.quote_ID,
          this.quote.quote_PaymentType_ID,
          this.quote.quote_PaymentTerm_ID,
          this.quote.quote_BidAmount,
          [])
          .pipe(finalize(() => this.isRequesting = false))
          .subscribe(
            {
              next: (result) => {
                this.router.navigate(['/']);
              },
              error: (error: string) => this.errors = error
            });
      });
  }

  openStatusDialog(): void {
    const dialogRef = this.dialog.open(QuoteStatusDialog, {
      width: '500px',
      data: {
        quote_ID: this.quote.quote_ID,
        quote_IsLost: this.quote.quote_IsLost,
        lostReasonType_ID: this.quote.lostReasonType_ID,
        competitor_ID: this.quote.competitor_ID,
        quote_LostNote: this.quote.quote_LostNote,

        lostReasonTypes: this.lostReasonTypes,
        competitors: this.competitors
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
      if (result) {
        if (result.lostReasonType_ID == '') result.lostReasonType_ID = null;
        if (result.competitor_ID == '') result.competitor_ID = null;

        result.quote_IsLost = (result.lostReasonType_ID != null);

        this.quoteService.updateQuoteLostStatus(this.quote.quote_ID, result)
          .subscribe({
            next: (data) => {
              this.updated.emit(this.quote);
            },
            error: (error: string) => this.onError(error)
          });
      } else {
        this.lostStatus = '';
      }
    });
  }

  deleteArea(quoteAreaItemId: number): void {
    var proceed = confirm($localize`Delete this quote area?`);
    if (!proceed) return;

    this.quoteService.deleteQuoteAreaItem(quoteAreaItemId)
      .subscribe({
        next: (result) => {
          this.refreshQuote(this.quote.quote_ID);
        },
        error: (error: string) => this.onError(error)
      });
  }

  openAreaDialog(quoteAreaItemId: number): void {
    const dialogRef = this.dialog.open(QuoteAreaDialog, {
      width: '900px',
      data: {
        areas: this.areas,
        quoteId: this.quote.quote_ID,
        quoteAreaItemId: quoteAreaItemId,
        showLinePricing: this.showLinePricing,
        quoteItemNoteTemplates: this.quoteItemNoteTemplates,
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The area dialog was closed');
      if (result) {
        this.refreshQuote(this.quote.quote_ID);
      };
    });
  }

  addUpgradeDialog(quoteArea: QuoteArea): void {
    const dialogRef = this.dialog.open(QuoteAreaDialog, {
      width: '900px',
      data: {
        areas: this.areas,
        quoteId: this.quote.quote_ID,
        quoteArea: quoteArea
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The area dialog was closed');
      if (result) {
        this.refreshQuote(this.quote.quote_ID);
      };
    });
  }

  openEmailDialog(): void {

    var contacts = new Array() as ContactSelect[];
    var quoteAssignments = new Array() as QuoteAssignmentSelect[];

    if (this.quote.customer.contacts != null && this.quote.customer.contacts != undefined) {
      this.quote.customer.contacts.forEach((contact) => {

        if (contact.contact_Email && contact.contact_IsActive) {
          var cntct = contact as ContactSelect;
          if (contact.contact_ID == this.quote.customer.contact_ID) {
            cntct.signer = true;
          }
          contacts.push(contact as ContactSelect);
        }
      });
    }

    if (this.quote.quoteAssignments != null && this.quote.quoteAssignments != undefined) {
      this.quote.quoteAssignments.forEach((assiged) => {

        if (assiged.assignedTo.email) {
          var assignee = assiged as QuoteAssignmentSelect;
          if (assignee.quote_AssignedTo == assiged.quote_AssignedTo) {
          }
          quoteAssignments.push(assignee as QuoteAssignmentSelect);
        }
      });
    }

    const dialogRef = this.dialog.open(QuoteEmailDialog, {
      width: (this.quote.dealer_DocusignEnabled ? '800px' : '500px'),
      data: {
        quote_ID: this.quote.quote_ID,
        quote_ContractNotes: this.quote.quote_ContractNotes,
        contacts: contacts,
        docuSignEnabled: this.quote.dealer_DocusignEnabled,
        existingRequest: true,
        sendNewRequestBlock: true,
        showPricing: false,
        showSquareFootage: false,
        showTaxes: false,
        hideLineItemNotes: false,
        quoteAssignments: quoteAssignments,        
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.refreshQuote(this.quote.quote_ID);
        this.quote.quote_ContractNotes = result.quote_ContractNotes;
      }
    });
  }

  openCopyDialog(): void {
    const dialogRef = this.dialog.open(QuoteCopyDialog, {
      width: '500px',
      data: {
        quote_ID: this.quote.quote_ID
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
      if (result) {
        this.quoteService.copyQuote(this.quote.quote_ID, result)
          .subscribe({
            next: (data) => {
              window.alert($localize`Quote copied`);
            },
            error: (error: string) => this.onError(error)
          });
      }
    });
  }

  openTemplateDialog(): void {
    const dialogRef = this.dialog.open(QuoteTemplateDialog, {
      width: '500px',
      data: {
        quote_ID: this.quote.quote_ID
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
      if (result) {
        window.alert($localize`Quote template created`);
      }
    });
  }

  convertQuote(): void {
    if (this.areaSummaries.length == 0) {
      alert($localize`This quote has no products defined.  Cannot convert a quote with no products assigned`);
      return;
    }

    const dialogRef = this.dialog.open(ConvertDialog, {
      maxWidth: '95vw',
      panelClass: 'custom-dialog-container',
      data: {
        quote: this.quote
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      console.log('The dialog was closed');
      if (result) {
        this.router.navigate(['/job/' + this.quote.quote_ID]);
      }
    });
  }

  returnToAdmin() {
    this.toggleTemplate.emit(this.quote.quote_ID);
  }
}

@Component({
  selector: 'quote.status.dialog',
  templateUrl: 'quote.status.dialog.html',
})
export class QuoteStatusDialog {

  constructor(
    public dialogRef: MatDialogRef<QuoteStatusDialog>,
    @Inject(MAT_DIALOG_DATA) public data: StatusDialogData) { }

  saveStatus(form: NgForm): void {
    if (form.valid) {
      this.dialogRef.close(this.data);
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

}

@Component({
  selector: 'quote.copy.dialog',
  templateUrl: 'quote.copy.dialog.html',
})
export class QuoteCopyDialog {

  constructor(
    public dialogRef: MatDialogRef<QuoteCopyDialog>,
    @Inject(MAT_DIALOG_DATA) public data: CopyDialogData) { }

  onNoClick(): void {
    this.dialogRef.close();
  }

}


@Component({
  selector: 'quote.email.dialog',
  templateUrl: 'quote.email.dialog.html',
})
export class QuoteEmailDialog implements OnInit, OnDestroy {

  constructor(private quoteService: QuoteService, private router: Router,
    public dialogRef: MatDialogRef<QuoteEmailDialog>,
    @Inject(MAT_DIALOG_DATA) public data: EmailDialogData) { }

  user_email: string = '';
  isRequesting: boolean = false;
  cc_selected: boolean = false;
  locale: string = 'en';
  showDocuments: boolean = false;
  showTaxes: boolean = false;
  groupAreas: boolean = false;
  dealerDocuments: DealerDocument[] = [];
  selectedDocuments: DealerDocument[] = [];
  selectedDocumentIDs: number[] = [];

  private usernameSubscription: Subscription;

  ngOnInit() {
    this.usernameSubscription = AuthService.authNavStatus$.subscribe(authenticated => {
      this.user_email = "";
      if (authenticated) {
        let user = AuthService._authUserSource.value;
        this.user_email = user.email;
      }
    });
  }

  ngOnDestroy() {
    this.usernameSubscription.unsubscribe();
  }

  sendEmail(form: NgForm): void {
    if (form.valid) {
      let contacts = this.data.contacts.filter(c => c.selected).map(c => c.contact_ID);

      if (contacts.length == 0 && !this.cc_selected) {
        alert($localize`No contacts selected`);
        return;
      }

      this.isRequesting = true;
      this.selectedDocuments.forEach(sd => {
        this.selectedDocumentIDs.push(sd.dealerDocument_ID);
      });

      let ccEmails = this.data.quoteAssignments?.filter(qa => qa.selected).map(c => c.assignedTo.email);

      this.quoteService.emailQuote(this.data.quote_ID, this.data.changeOrder_ID, contacts, this.cc_selected, ccEmails,
        this.data.quoteEmail_Message, this.data.quote_ContractNotes, this.data.showPricing, this.data.showSquareFootage, this.showTaxes, this.locale, this.selectedDocumentIDs, this.groupAreas)
        .subscribe({
          next: (result) => {
            this.isRequesting = false;
            alert($localize`Email has been sent`);
            this.dialogRef.close(this.data);

            return;
          },
          error: (error: string) => this.onError(error),
          complete: () => {
            this.isRequesting = false;
            return;
          }
        });
    }
  }

  showQuoteDocuments() {
    this.showDocuments = true;
  }

  documentSelectionUpdated(documentsSelected: DealerDocument[]) {
    this.selectedDocuments = documentsSelected;
  }

  loadDocuments(loadedDocuments: DealerDocument[]) {
    this.dealerDocuments = loadedDocuments;
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  onError(response) {
    this.isRequesting = false;
    window.alert(response);
    console.log(response);
  }

}
